import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { getProjects } from "@/lib/api";
import { Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const categories = ["all", "Recherche", "Innovation", "Collaboration"];

export default function ProjectsSection() {
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    getProjects(activeCategory === "all" ? null : activeCategory)
      .then((res) => setProjects(res.data))
      .catch(() => {});
  }, [activeCategory]);

  return (
    <section id="projects" data-testid="projects-section" className="py-24 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#1670B9] mb-4">
            Projets & Publications
          </p>
          <h2
            className="font-['Quicksand'] text-3xl lg:text-4xl font-medium tracking-tight text-slate-800"
            data-testid="projects-title"
          >
            Nos travaux de recherche
          </h2>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="project-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-xl text-sm font-medium transition-all ${
                activeCategory === cat
                  ? "bg-[#1670B9] text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-[#1670B9] hover:text-[#1670B9]"
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat === "all" ? "Tous" : cat}
            </button>
          ))}
        </div>

        {/* Projects grid */}
        {projects.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <p className="text-lg">Aucun projet pour le moment.</p>
            <p className="text-sm mt-2">Les projets seront ajoutés via le panneau d'administration.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, i) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-white rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                data-testid={`project-card-${i}`}
              >
                {project.image_url && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 rounded-lg bg-[#1670B9]/10 text-[#1670B9] text-xs font-semibold">
                      {project.category}
                    </span>
                    {project.created_at && (
                      <span className="flex items-center text-xs text-slate-400 gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(project.created_at).toLocaleDateString("fr-FR")}
                      </span>
                    )}
                  </div>
                  <h3 className="font-['Quicksand'] text-lg font-semibold text-slate-800 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-3">
                    {project.summary}
                  </p>
                  <Link to={`/project/${project.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#1670B9] hover:gap-3 transition-all">
                    Lire plus <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
